import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import { ErrorBox } from '../components/ui';

/**
 * New Programme form. A programme needs a client and a main contractor,
 * both organisations. Rather than force the user to leave and create those
 * first, the org pickers have an inline "add new" so the whole thing can be
 * done in one place — the common case when onboarding a fresh programme.
 */
export default function NewProgramme() {
  const nav = useNavigate();
  const [clients, setClients] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [form, setForm] = useState({ reference: '', name: '', clientOrgId: '', contractorOrgId: '', architectOrgId: '', fireEngineerOrgId: '', brpdOrgId: '', dryliningOrgId: '', startDate: '', expectedEndDate: '', description: '' });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [duration, setDuration] = useState(null);

  const loadOrgs = () => {
    api.get('/organisations', { params: { type: 'CLIENT' } }).then(r => setClients(r.data)).catch(setErr);
    api.get('/organisations', { params: { type: 'CONTRACTOR' } }).then(r => setContractors(r.data)).catch(setErr);
    api.get('/organisations', { params: { type: 'CONSULTANT' } }).then(r => setConsultants(r.data)).catch(() => {});
  };
  useEffect(() => {
    loadOrgs();
    api.get('/suggest-reference', { params: { kind: 'programme' } }).then(r => setForm(f => ({ ...f, reference: r.data.reference }))).catch(() => {});
  }, []);

  // Show the derived duration (working days) the moment both dates are set —
  // the same "weeks and days" the programme record will carry.
  useEffect(() => {
    if (form.startDate && form.expectedEndDate) {
      const s = new Date(form.startDate), e = new Date(form.expectedEndDate);
      if (e >= s) {
        let wd = 0; const d = new Date(s);
        while (d <= e) { const day = d.getDay(); if (day !== 0 && day !== 6) wd++; d.setDate(d.getDate() + 1); }
        setDuration(`${Math.floor(wd / 5)} wks ${wd % 5} days (${wd} working days)`);
      } else setDuration('End date is before start date');
    } else setDuration(null);
  }, [form.startDate, form.expectedEndDate]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const roleLabels = { CLIENT: 'client', CONTRACTOR: 'main contractor', CONSULTANT: 'consultant' };
  const addOrg = async (type, field) => {
    const name = window.prompt(`Name of the new ${roleLabels[type] || 'organisation'}:`);
    if (!name) return;
    try {
      const r = await api.post('/organisations', { name, type });
      loadOrgs();
      if (field) set(field, String(r.data.id));
      else set(type === 'CLIENT' ? 'clientOrgId' : 'contractorOrgId', String(r.data.id));
    } catch (e) { setErr(e); }
  };

  const submit = async e => {
    e.preventDefault(); setErr(null);
    if (!form.clientOrgId) return setErr(new Error('Choose or add a client'));
    if (!form.contractorOrgId) return setErr(new Error('Choose or add a main contractor'));
    if (!form.architectOrgId || !form.fireEngineerOrgId || !form.brpdOrgId || !form.dryliningOrgId)
      return setErr(new Error('Architect, Fire Engineer, BRPD and Drylining consultant must all be indicated'));
    setBusy(true);
    try {
      const r = await api.post('/programmes', {
        reference: form.reference, name: form.name,
        clientOrgId: Number(form.clientOrgId), contractorOrgId: Number(form.contractorOrgId),
        architectOrgId: Number(form.architectOrgId) || null, fireEngineerOrgId: Number(form.fireEngineerOrgId) || null,
        brpdOrgId: Number(form.brpdOrgId) || null, dryliningOrgId: Number(form.dryliningOrgId) || null,
        startDate: form.startDate || null, expectedEndDate: form.expectedEndDate || null,
        description: form.description || null
      });
      nav(`/programmes/${r.data.id}`);
    } catch (e2) { setErr(e2); setBusy(false); }
  };

  return (
    <>
      <div className="phead"><h1>New programme</h1></div>
      <div className="panel">
        <ErrorBox error={err} />
        <form onSubmit={submit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
            <div className="field"><label>Reference</label>
              <input style={{ width: '100%' }} value={form.reference} onChange={e => set('reference', e.target.value)} required /></div>
            <div className="field"><label>Programme name</label>
              <input style={{ width: '100%' }} value={form.name} onChange={e => set('name', e.target.value)} required /></div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="field"><label>Client</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <select style={{ flex: 1 }} value={form.clientOrgId} onChange={e => set('clientOrgId', e.target.value)}>
                  <option value="">Choose…</option>
                  {clients.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
                <button type="button" onClick={() => addOrg('CLIENT')}>+ New</button>
              </div>
            </div>
            <div className="field"><label>Main contractor</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <select style={{ flex: 1 }} value={form.contractorOrgId} onChange={e => set('contractorOrgId', e.target.value)}>
                  <option value="">Choose…</option>
                  {contractors.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
                <button type="button" onClick={() => addOrg('CONTRACTOR')}>+ New</button>
              </div>
              <div className="muted" style={{ marginTop: 3 }}>Covers all work on the programme by default.</div>
            </div>
          </div>


          <div style={{ borderTop: '1px solid var(--line)', paddingTop: 12, marginTop: 4, marginBottom: 4 }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Appointments</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {[['architectOrgId','Architect / Lead Designer'],['fireEngineerOrgId','Fire Engineer'],['brpdOrgId','BRPD (submits to regulator)'],['dryliningOrgId','Drylining consultant']].map(([field,label]) => (
                <div className="field" key={field}><label>{label}</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <select style={{ flex: 1 }} value={form[field]} onChange={e => set(field, e.target.value)}>
                      <option value="">Choose…</option>
                      {consultants.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                    <button type="button" onClick={() => addOrg('CONSULTANT', field)}>+ New</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.4fr', gap: 12 }}>
            <div className="field"><label>Start date</label>
              <input type="date" style={{ width: '100%' }} value={form.startDate} onChange={e => set('startDate', e.target.value)} /></div>
            <div className="field"><label>Expected end date</label>
              <input type="date" style={{ width: '100%' }} value={form.expectedEndDate} onChange={e => set('expectedEndDate', e.target.value)} /></div>
            <div className="field"><label>Duration</label>
              <div style={{ height: 34, display: 'flex', alignItems: 'center', padding: '0 10px', background: 'var(--track-bg)', borderRadius: 5, fontSize: 12.5 }}>
                {duration || '—'}</div></div>
          </div>

          <div className="field"><label>Description (optional)</label>
            <textarea style={{ width: '100%', minHeight: 60 }} value={form.description} onChange={e => set('description', e.target.value)} /></div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button className="primary" disabled={busy}>{busy ? 'Creating…' : 'Create programme'}</button>
            <button type="button" onClick={() => nav('/programmes')}>Cancel</button>
          </div>
          <p className="muted" style={{ marginBottom: 0 }}>After creating the programme, add projects (sites) to it — each project generates its own plan.</p>
        </form>
      </div>
    </>
  );
}
